import { useTenantStore } from '@/store/tenantStore';
import { detectTenantSlugFromHostname } from '@/utils/tenantDetect';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Lazy imports to avoid circular dependency at module load time.
// We call getState() only at request time (after all modules have initialised).

type RequestOptions = {
  method?: string;
  body?: unknown;
  headers?: Record<string, string>;
};

// Get tokens from localStorage (auth-storage key used by Zustand persist)
function getAuthTokens(): { accessToken?: string; refreshToken?: string } {
  try {
    const raw = localStorage.getItem('auth-storage');
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return {
      accessToken: parsed?.state?.accessToken,
      refreshToken: parsed?.state?.refreshToken,
    };
  } catch {
    return {};
  }
}

function getTenantSlug(): string {
  // After fetchTenant() completes, the store has the real slug.
  // On the very first call (before the fetch resolves) we fall back to
  // hostname-based detection which mirrors the backend middleware logic.
  return useTenantStore.getState().tenant?.slug ?? detectTenantSlugFromHostname();
}

function getLanguage(): string {
  try {
    const raw = localStorage.getItem('language-storage');
    if (!raw) return 'en';
    const parsed = JSON.parse(raw);
    return parsed?.state?.language || 'en';
  } catch {
    return 'en';
  }
}

let isRefreshing = false;
let refreshPromise: Promise<string | null> | null = null;

async function refreshAccessToken(): Promise<string | null> {
  const { refreshToken } = getAuthTokens();
  if (!refreshToken) return null;

  try {
    const res = await fetch(`${API_BASE}/v1/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Tenant-Slug': getTenantSlug() },
      body: JSON.stringify({ refreshToken }),
    });

    if (!res.ok) {
      // Refresh failed — clear auth so user re-authenticates
      localStorage.removeItem('auth-storage');
      return null;
    }

    // Backend wraps responses in { success, data } — unwrap before reading tokens
    const json = await res.json();
    const tokens = json?.data ?? json;
    const raw = localStorage.getItem('auth-storage');
    if (raw) {
      const parsed = JSON.parse(raw);
      parsed.state.accessToken = tokens.accessToken;
      if (tokens.refreshToken) parsed.state.refreshToken = tokens.refreshToken;
      localStorage.setItem('auth-storage', JSON.stringify(parsed));
    }
    return tokens.accessToken;
  } catch {
    return null;
  }
}

export async function apiFetch<T = unknown>(
  endpoint: string,
  options: RequestOptions = {}
): Promise<T> {
  const { method = 'GET', body, headers = {} } = options;
  const { accessToken } = getAuthTokens();

  const reqHeaders: Record<string, string> = {
    'Content-Type': 'application/json',
    'X-Tenant-Slug': getTenantSlug(),
    'Accept-Language': getLanguage(),
    ...headers,
  };

  if (accessToken) {
    reqHeaders['Authorization'] = `Bearer ${accessToken}`;
  }

  let res = await fetch(`${API_BASE}${endpoint}`, {
    method,
    headers: reqHeaders,
    body: body ? JSON.stringify(body) : undefined,
  });

  // Auto-refresh on 401
  if (res.status === 401 && accessToken) {
    // Deduplicate concurrent refresh attempts
    if (!isRefreshing) {
      isRefreshing = true;
      refreshPromise = refreshAccessToken().finally(() => {
        isRefreshing = false;
      });
    }

    const newToken = await refreshPromise;
    refreshPromise = null;

    if (!newToken) {
      // Both tokens expired — force redirect to login
      localStorage.removeItem('auth-storage');
      window.location.href = '/auth/login';
      throw new ApiError('Session expired. Please log in again.', 401);
    }

    reqHeaders['Authorization'] = `Bearer ${newToken}`;
    res = await fetch(`${API_BASE}${endpoint}`, {
      method,
      headers: reqHeaders,
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  const data = await res.json();

  if (!res.ok) {
    throw new ApiError(data.message || data.error || 'Something went wrong. Please try again.', res.status, data);
  }

  return data as T;
}

export class ApiError extends Error {
  status: number;
  data: unknown;

  constructor(message: string, status: number, data?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
  }
}

// Create default export with common HTTP methods
// V1 backend wraps responses in { success, data, message }, so we unwrap them
const api = {
  get: async <T = unknown>(endpoint: string): Promise<T> => {
    const response = await apiFetch<{ success: boolean; data: T; message?: string }>(endpoint, { method: 'GET' });
    return response.data;
  },
  post: async <T = unknown>(endpoint: string, data?: unknown): Promise<T> => {
    const response = await apiFetch<{ success: boolean; data: T; message?: string }>(endpoint, { method: 'POST', body: data });
    return response.data;
  },
  patch: async <T = unknown>(endpoint: string, data?: unknown): Promise<T> => {
    const response = await apiFetch<{ success: boolean; data: T; message?: string }>(endpoint, { method: 'PATCH', body: data });
    return response.data;
  },
  delete: async <T = unknown>(endpoint: string): Promise<T> => {
    const response = await apiFetch<{ success: boolean; data: T; message?: string }>(endpoint, { method: 'DELETE' });
    return response.data;
  },
  put: async <T = unknown>(endpoint: string, data?: unknown): Promise<T> => {
    const response = await apiFetch<{ success: boolean; data: T; message?: string }>(endpoint, { method: 'PUT', body: data });
    return response.data;
  },
};

/**
 * Get headers for multipart/form-data uploads (FormData)
 * Includes auth token and tenant slug but NOT Content-Type (browser sets it with boundary)
 */
export function getMultipartHeaders(): Record<string, string> {
  const { accessToken } = getAuthTokens();
  const headers: Record<string, string> = {
    'X-Tenant-Slug': getTenantSlug(),
    'Accept-Language': getLanguage(),
  };
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }
  return headers;
}

export default api;
